// Phase 9A — useSignalTimeline hook.
//
// Reads signal_timeline_events for a given signal. Shows the full
// lifecycle history: creation, probability updates, status changes.
//
// Trust contract:
//   - Read-only. RLS enforced via signal_cards ownership join.
//   - No writes, no randomness.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface SignalTimelineEvent {
  id: string;
  signal_id: string;
  event_type: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
}

export interface UseSignalTimelineState {
  events: SignalTimelineEvent[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function asJsonbObj(v: unknown): Record<string, unknown> | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

export function useSignalTimeline(signalId: string | null): UseSignalTimelineState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [events, setEvents] = useState<SignalTimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user || signalId === null) {
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);

    const fromFn = supabase.from as unknown as (
      table: string,
    ) => {
      select: (cols: string) => {
        eq: (col: string, val: string) => {
          order: (col: string, opts: { ascending: boolean }) => Promise<{
            data: Record<string, unknown>[] | null;
            error: { message: string } | null;
          }>;
        };
      };
    };

    const res = await fromFn("signal_timeline_events")
      .select("*")
      .eq("signal_id", signalId)
      .order("created_at", { ascending: true });

    if (res.error) {
      setError(res.error.message);
      setEvents([]);
      setLoading(false);
      return;
    }

    const rows = (res.data ?? []).map((r): SignalTimelineEvent => ({
      id: String(r.id ?? ""),
      signal_id: String(r.signal_id ?? ""),
      event_type: String(r.event_type ?? ""),
      old_value: asJsonbObj(r.old_value),
      new_value: asJsonbObj(r.new_value),
      reason: typeof r.reason === "string" ? r.reason : null,
      created_at: String(r.created_at ?? ""),
    }));

    setEvents(rows);
    setLoading(false);
  }, [supabase, user, signalId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseSignalTimelineState>(
    () => ({ events, loading, error, refetch }),
    [events, loading, error, refetch],
  );
}
